import { allTypingInstructions } from "@/lib/symbol-typing";

interface Props {
  symbol: string;
  name: string;
}

/**
 * Per-OS typing instructions derived from the symbol's codepoints. Pure
 * presentation — all logic lives in `lib/symbol-typing.ts`.
 */
export function SymbolHowToType({ symbol, name }: Props) {
  const blocks = allTypingInstructions(symbol);

  return (
    <section
      aria-labelledby="howto-heading"
      className="mx-auto w-full max-w-5xl px-4 pt-10 sm:px-6 sm:pt-12"
    >
      <h2 id="howto-heading" className="font-display text-xl font-semibold sm:text-2xl">
        How to type {name}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Native keyboard shortcuts for each operating system — derived from the
        codepoint, so the steps below are always accurate.
      </p>
      <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {blocks.map((b) => (
          <article
            key={b.os}
            className="glass rounded-xl p-4"
            aria-label={`${b.os} typing instructions`}
          >
            <header className="flex items-baseline justify-between">
              <h3 className="font-display text-base font-semibold">{b.os}</h3>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                {b.steps.length === 1 ? "1 step" : `${b.steps.length} options`}
              </span>
            </header>
            <ol className="mt-3 space-y-2 text-sm text-foreground/90">
              {b.steps.map((step, i) => (
                <li key={i} className="flex gap-2">
                  <span
                    aria-hidden="true"
                    className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full border border-border bg-surface/60 text-[10px] font-semibold text-muted-foreground"
                  >
                    {i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
            {b.note ? (
              <p className="mt-3 text-xs text-muted-foreground">{b.note}</p>
            ) : null}
          </article>
        ))}
      </div>
    </section>
  );
}
